import React, { useState } from "react";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Badge, 
  Alert,
  Modal,
  ProgressBar
} from "react-bootstrap";
import {
  Calendar,
  Clock,
  Search,
  Download,
  Printer,
  InfoCircle,
  PersonCircle,
  People,
  FileText,
  ChevronDown,
  ChevronUp,
  Eye,
  Filter,
  Activity,
  Heart,
  Pill,
  Microscope,
  GraphUp,
  Award,
  CheckCircle
} from "react-bootstrap-icons";
import { useAuth } from "../../contexts/AuthContext";

// Données fictives des consultations
const mockConsultations = [
  { 
    id: "C001", 
    date: "2025-03-12", 
    time: "10:30",
    doctor: "Dr. Nzogho", 
    specialty: "Médecine générale",
    reason: "Fièvre et maux de tête",
    diagnosis: "Grippe saisonnière",
    prescription: "Paracétamol 500mg, Vitamine C",
    notes: "Repos et hydratation abondante",
    status: "Terminé",
    followUp: "25/03/2025"
  },
  { 
    id: "C002", 
    date: "2025-03-01", 
    time: "14:00",
    doctor: "Dr. Mba", 
    specialty: "Cardiologie",
    reason: "Contrôle annuel",
    diagnosis: "Normal",
    prescription: "Aucun traitement",
    notes: "Bilan cardiaque satisfaisant",
    status: "Terminé",
    followUp: "01/03/2026"
  },
  { 
    id: "C003", 
    date: "2025-02-20", 
    time: "09:00",
    doctor: "Dr. Ondo", 
    specialty: "Dermatologie",
    reason: "Éruption cutanée",
    diagnosis: "Allergie légère",
    prescription: "Cetirizine 10mg",
    notes: "Éviter les allergènes potentiels",
    status: "Terminé",
    followUp: "20/03/2025"
  },
  { 
    id: "C004", 
    date: "2025-02-10", 
    time: "11:15",
    doctor: "Dr. Nzogho", 
    specialty: "Médecine générale",
    reason: "Toux persistante",
    diagnosis: "Bronchite",
    prescription: "Amoxicilline 500mg",
    notes: "Repos et suivi en 5 jours",
    status: "Terminé",
    followUp: "15/02/2025"
  },
  { 
    id: "C005", 
    date: "2025-01-25", 
    time: "15:30",
    doctor: "Dr. Mba", 
    specialty: "Cardiologie",
    reason: "Palpitations",
    diagnosis: "Stress",
    prescription: "Relaxation, gestion du stress",
    notes: "Consultation de suivi recommandée",
    status: "Terminé",
    followUp: "25/03/2025"
  },
  { 
    id: "C006", 
    date: "2025-01-15", 
    time: "08:45",
    doctor: "Dr. Nzogho", 
    specialty: "Médecine générale",
    reason: "Vaccination annuelle",
    diagnosis: "Vaccination grippe",
    prescription: "Vaccin grippe",
    notes: "Prochain rappel dans 1 an",
    status: "Terminé",
    followUp: "15/01/2026"
  }
];

const ConsultationHistory = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString, timeString) => {
    return `${formatDate(dateString)} à ${timeString}`;
  };

  const getStatusBadge = (status) => {
    return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Terminé</Badge>;
  };

  // Filtrage
  const filteredConsultations = mockConsultations.filter(c => {
    const matchesSearch = searchTerm === '' || 
      c.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.diagnosis.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Tri
  const sortedConsultations = [...filteredConsultations].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    if (sortField === 'date') {
      aValue = new Date(a.date);
      bValue = new Date(b.date);
    }
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Statistiques
  const stats = {
    total: mockConsultations.length,
    thisYear: mockConsultations.filter(c => new Date(c.date).getFullYear() === 2025).length,
    thisMonth: mockConsultations.filter(c => {
      const d = new Date(c.date);
      return d.getMonth() === 2 && d.getFullYear() === 2025;
    }).length,
    mostFrequentDoctor: "Dr. Nzogho",
    mostFrequentSpecialty: "Médecine générale"
  };

  // Cartes de statistiques
  const statsCards = [
    { 
      title: "Total consultations", 
      value: stats.total, 
      icon: People, 
      color: "#4361ee", 
      gradient: "linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)",
      bg: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
    },
    { 
      title: "Cette année", 
      value: stats.thisYear, 
      icon: Calendar, 
      color: "#10b981", 
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
    },
    { 
      title: "Ce mois-ci", 
      value: stats.thisMonth, 
      icon: GraphUp, 
      color: "#f59e0b", 
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)"
    }
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><a href="/patient">Tableau de bord</a></li>
                <li className="breadcrumb-item active">Historique des consultations</li>
              </ol>
              <h4 className="main-title mb-0 d-flex align-items-center gap-2">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                  <People size={24} className="text-primary" />
                </div>
                Historique des consultations
              </h4>
              <p className="text-muted mb-0 mt-1">
                Consultez l'historique complet de vos visites médicales
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" className="rounded-pill px-3">
                <Download size={14} className="me-1" /> Exporter
              </Button>
              <Button variant="outline-secondary" size="sm" className="rounded-pill px-3">
                <Printer size={14} className="me-1" /> Imprimer
              </Button>
            </div>
          </div>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={4} xs={6} key={idx}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card h-100 transition-all" style={{ borderRadius: '16px', backdropFilter: 'blur(0px)', background: 'white' }}>
                <div className="stats-card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase tracking-wide" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-1 fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2, color: '#1f2937' }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="stats-icon rounded-3 d-flex align-items-center justify-content-center flex-shrink-0" 
                         style={{ background: card.gradient, width: '44px', height: '44px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                      <card.icon size={22} className="text-white" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtres */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px' }}>
              <Card.Body>
                <Row className="g-3">
                  <Col md={6} xs={6}>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-0">
                        <Search size={16} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Rechercher par médecin, spécialité, motif ou diagnostic..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-0 bg-light"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4} xs={6}>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-0">
                        <Filter size={16} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border-0 bg-light"
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="Terminé">Terminées</option>
                      </Form.Select>
                    </InputGroup>
                  </Col>
                  <Col md={2}>
                    <Button 
                      variant="outline-secondary" 
                      className="w-100 rounded-pill"
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                      }}
                    >
                      Réinitialiser
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tableau des consultations avec adaptation mobile */}
        <Row>
          <Col md={12}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-primary text-white py-3">
                <div className="d-flex align-items-center gap-2">
                  <Activity size={20} />
                  <span className="fw-semibold">
                    Consultations ({sortedConsultations.length})
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <SortableHeader field="date">Date</SortableHeader>
                        <SortableHeader field="doctor">Médecin</SortableHeader>
                        <th className="d-none d-sm-table-cell">Spécialité</th>
                        <th className="d-none d-sm-table-cell">Motif</th>
                        <th className="d-none d-sm-table-cell">Diagnostic</th>
                        <th className="d-none d-sm-table-cell">Statut</th>
                        <th className="d-none d-sm-table-cell">Action</th>
                        <th className="d-sm-none">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedConsultations.length > 0 ? (
                        sortedConsultations.map(c => {
                          const isExpanded = expandedRows.has(c.id);
                          return (
                            <React.Fragment key={c.id}>
                              {/* Ligne principale */}
                              <tr className="align-middle">
                                <td data-label="Date" className="small">
                                  <div className="fw-medium">{formatDate(c.date)}</div>
                                  <small className="text-muted">{c.time}</small>
                                </td>
                                <td data-label="Médecin" className="fw-medium">{c.doctor}</td>
                                <td data-label="Spécialité" className="d-none d-sm-table-cell">{c.specialty}</td>
                                <td data-label="Motif" className="d-none d-sm-table-cell">{c.reason}</td>
                                <td data-label="Diagnostic" className="d-none d-sm-table-cell">{c.diagnosis}</td>
                                <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(c.status)}</td>
                                <td data-label="Action" className="d-none d-sm-table-cell">
                                  <Button 
                                    size="sm" 
                                    variant="outline-primary"
                                    className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                    style={{ width: '32px', height: '32px' }}
                                    onClick={() => {
                                      setSelectedConsultation(c);
                                      setShowDetailsModal(true);
                                    }}
                                  >
                                    <Eye size={14} />
                                  </Button>
                                </td>
                                <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                  <Button
                                    variant="link"
                                    onClick={() => toggleRow(c.id)}
                                    className="p-0 text-secondary"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </Button>
                                </td>
                              </tr>
                              {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                              {isExpanded && (
                                <tr className="d-sm-none detail-row">
                                  <td colSpan="8" className="p-3 bg-light">
                                    <div className="row g-2">
                                      <div className="col-6"><strong>Spécialité :</strong> {c.specialty}</div>
                                      <div className="col-6"><strong>Motif :</strong> {c.reason}</div>
                                      <div className="col-6"><strong>Diagnostic :</strong> {c.diagnosis}</div>
                                      <div className="col-6"><strong>Statut :</strong> {getStatusBadge(c.status)}</div>
                                      <div className="col-12 mt-2">
                                        <Button 
                                          size="sm" 
                                          variant="outline-primary"
                                          className="rounded-pill"
                                          onClick={() => {
                                            setSelectedConsultation(c);
                                            setShowDetailsModal(true);
                                          }}
                                        >
                                          <Eye size={14} className="me-1" /> Voir détails
                                        </Button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="bg-light rounded-circle p-3 d-inline-block mb-3">
                              <People size={48} className="text-muted" />
                            </div>
                            <p className="text-muted mb-0">Aucune consultation trouvée</p>
                            {(searchTerm || statusFilter !== 'all') && (
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="mt-2"
                                onClick={() => {
                                  setSearchTerm('');
                                  setStatusFilter('all');
                                }}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal des détails (inchangé) */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <FileText size={20} />
              Détails de la consultation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedConsultation && (
              <>
                <Row className="g-4">
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Date et heure</small>
                      <strong>{formatDateTime(selectedConsultation.date, selectedConsultation.time)}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Médecin</small>
                      <strong>{selectedConsultation.doctor}</strong>
                      <div className="small text-muted">{selectedConsultation.specialty}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Motif de consultation</small>
                      <strong>{selectedConsultation.reason}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Diagnostic</small>
                      <strong>{selectedConsultation.diagnosis}</strong>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Prescription</small>
                      <strong>{selectedConsultation.prescription}</strong>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Notes du médecin</small>
                      <p className="mb-0">{selectedConsultation.notes}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Prochain rendez-vous</small>
                      <strong>{formatDate(selectedConsultation.followUp)}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <small className="text-muted d-block mb-2">Statut</small>
                      {getStatusBadge(selectedConsultation.status)}
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Fermer
            </Button>
            <Button variant="primary">
              <Download size={14} className="me-1" />
              Télécharger le compte-rendu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: "linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)" }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Votre historique médical</strong>
                  <span className="small">Cette section contient l'ensemble de vos consultations médicales. Pour toute erreur ou information manquante, veuillez contacter votre médecin traitant.</span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .nav-tabs .nav-link {
            border: none;
            color: #6c757d;
            padding: 1rem 1rem;
            font-weight: 500;
            transition: all 0.2s;
          }
          .nav-tabs .nav-link:hover {
            border: none;
            color: #0d6efd;
            background: transparent;
          }
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            border-bottom: 3px solid #0d6efd;
            background: transparent;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .stats-card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .stats-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          .display-6 {
            font-size: 1.75rem;
            font-weight: 600;
          }
          @media (max-width: 768px) {
            .stats-icon {
              width: 40px;
              height: 40px;
            }
            .stats-icon svg {
              width: 20px;
              height: 20px;
            }
            .display-6 {
              font-size: 1.5rem;
            }
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
            .detail-row td {
              padding: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ConsultationHistory;